/**
 * Meta Ads - Lead Generation Performance Dashboard
 *
 * Executive Summary section showing key Meta Ads KPIs.
 *
 * Data source: metaLeadGen/ isolated backend module.
 * No hardcoded values. No shared dashboard metrics. No external dependencies.
 *
 * To connect live Meta API data:
 *   - Replace mock data in metaLeadGen/mockData.ts with adaptMetaApiResponse()
 *   - No changes needed here.
 */

import { useEffect, useMemo } from "react";
import { useCrmFunnelData } from "./metaLeadGen/useCrmFunnelData";
import { useMetaAttributionData } from "./metaLeadGen/useMetaAttributionData";
import { useCloseCreativeAttribution } from "./metaLeadGen/useCloseCreativeAttribution";
import { useEocAttribution } from "./metaLeadGen/useEocAttribution";
import { useOptinAdAttribution } from "./metaLeadGen/useOptinAdAttribution";
import { motion } from "framer-motion";
import { PageHeader } from "../../components";
import { useMetaSheetsData } from "../../hooks/useMetaSheetsData";
import { useDateRange } from "../../context/DateRangeContext";

// ─────────────────────────────────────────────
// ISOLATED BACKEND IMPORTS ONLY
// ─────────────────────────────────────────────
import { getMetaLeadGenComparison } from "./metaLeadGen/service";
import { RAW_META_LEAD_GEN_ROWS } from "./metaLeadGen/mockData";
import { applyFilters } from "./metaLeadGen/filters";
import {
  formatCurrency,
  formatCpm,
  formatPercent,
  formatNumber,
} from "./metaLeadGen/formatters";
import { useMetaLeadGenFilters } from "./metaLeadGen/useFilters";
import { MetaLeadGenFilterBar } from "./metaLeadGen/FilterBar";
import { getDailyMetrics } from "./metaLeadGen/dailyMetrics";
import { ComboChart } from "./metaLeadGen/components/ComboChart";
import { branding } from "../../config/branding";
import { FunnelKpiSection, FunnelRow1 } from "./metaLeadGen/components/FunnelKpiSection";
import { getUnifiedFunnelData } from "./metaLeadGen/unifiedFunnelService";
import { KpiCard, SmallDarkCard } from "./metaLeadGen/components/KpiCards";
import { AgeBreakdownSection } from "./metaLeadGen/components/AgeBreakdownSection";
import { getAgeBreakdown } from "./metaLeadGen/ageBreakdown";
import { GenderBreakdownSection } from "./metaLeadGen/components/GenderBreakdownSection";
import { getGenderSummary, getGenderDailyData } from "./metaLeadGen/genderBreakdown";
import { DeviceBreakdownSection } from "./metaLeadGen/components/DeviceBreakdownSection";
import { getDeviceDailyData } from "./metaLeadGen/deviceBreakdown";
import { IndividualCreativeFunnelSection } from "./metaLeadGen/components/IndividualCreativeFunnelSection";
import { CreativePerformanceSection } from "./metaLeadGen/components/CreativePerformanceSection";
import { getCreativePerformanceData } from "./metaLeadGen/creativePerformanceData";
import { CreativeHeatmapSection } from "./metaLeadGen/components/CreativeHeatmapSection";
import { CampaignFrequencyChart } from "./metaLeadGen/components/CampaignFrequencyChart";
import { AdsOverview } from "./AdsOverview";

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export function MetaAds({ initialTab = "performance" }: { initialTab?: "overview" | "performance" | "breakdown" | "creative" }) {
  // ── Live data from Google Sheets via Apps Script Web App ──────────────────
  const {
    kpiRows,
    ageGenderRows,
    deviceRows,
    loading: liveLoading,
    error: liveError,
    debugInfo: liveDebugInfo,
  } = useMetaSheetsData();

  // Use live rows when available, fall back to mock data
  const isLive = kpiRows.length > 0;
  const activeKpiRows    = kpiRows.length > 0 ? kpiRows : RAW_META_LEAD_GEN_ROWS;
  // For breakdown sections: use live data when available, never mix with mock
  const activeAgeRows    = isLive ? ageGenderRows : RAW_META_LEAD_GEN_ROWS;
  const activeDeviceRows = isLive ? deviceRows    : RAW_META_LEAD_GEN_ROWS;

  // ── Filter state — entity options (campaign/adset/ad) from kpiRows only ───
  const allLiveRows = isLive ? kpiRows : undefined;
  const {
    state: filterState,
    filters,
    allOptions,
    adsetOptions,
    adOptions,
    update,
    setCampaignIds,
    setAdsetIds,
    reset: resetFilters,
    hasActiveFilters,
  } = useMetaLeadGenFilters(allLiveRows);

  // ── Dimension options sourced from the correct breakdown table ─────────────
  // Age/Gender options from ageGenderRows, Device options from deviceRows.
  // This ensures the dropdowns show real values and excludes dummy "all" entries.
  const mergedOptions = useMemo(() => {
    if (!isLive) return allOptions;
    const ages    = Array.from(new Set(ageGenderRows.map(r => r.age)))
                        .filter(a => a && a !== "all" && a !== "unknown")
                        .sort();
    const genders = Array.from(new Set(ageGenderRows.map(r => r.gender))).sort();
    const devices = Array.from(new Set(deviceRows.map(r => r.device))).sort();
    return { ...allOptions, ages, genders, devices };
  }, [isLive, allOptions, ageGenderRows, deviceRows]);

  // ── Global date range (top-right "All Time" picker) ────────────────────────
  const { dateRange: globalDateRange, setDateRange: setGlobalDateRange } = useDateRange();

  // Helper: Date → "YYYY-MM-DD" using local time
  const toYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Sync global date → Meta Ads filter whenever global date changes
  useEffect(() => {
    update({ dateRange: { start: toYMD(globalDateRange.startDate), end: toYMD(globalDateRange.endDate) } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDateRange.startDate, globalDateRange.endDate]);

  // ── Dimension-specific filter subsets ─────────────────────────────────────
  // kpiRows (raw_ads) have no age/gender/device dimensions — strip those to
  // avoid filtering out all KPI data when the user picks an age or gender.
  const kpiFilters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ages: _a, genders: _g, devices: _d, ...rest } = filters;
    return rest;
  }, [filters]);

  // ageGenderRows have no device dimension — strip device to avoid empty breakdown.
  const demographicFilters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { devices: _d, ...rest } = filters;
    return rest;
  }, [filters]);

  // deviceRows have no age/gender dimensions — strip them to avoid empty breakdown.
  const deviceOnlyFilters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ages: _a, genders: _g, ...rest } = filters;
    return rest;
  }, [filters]);

  // ── All data from isolated metaLeadGen backend ─────────────────────────────
  const { current, previous, changes } = getMetaLeadGenComparison(kpiFilters, activeKpiRows);

  // ── Real CRM funnel data from MongoDB ─────────────────────────────────────
  const crmFunnel = useCrmFunnelData(kpiFilters.dateRange.start, kpiFilters.dateRange.end);

  // ── Meta-attributed applications (optin→application cross-match) ───────────
  const metaAttribution = useMetaAttributionData(kpiFilters.dateRange.start, kpiFilters.dateRange.end);

  // ── Close CRM creative attribution (per-creative leads/apps/closes) ────────
  const closeCreative = useCloseCreativeAttribution();

  // ── EOC attribution (closes + cash from EOC report, matched by sourceContent) ──
  const eocAttribution = useEocAttribution(kpiFilters.dateRange.start, kpiFilters.dateRange.end);

  // ── Optin → Ad attribution (real optin counts per utm_content matched to ads) ──
  const optinAdAttribution = useOptinAdAttribution();

  // ── Unified funnel (Meta spend + CRM stages — still used by FunnelKpiSection) ──
  const unifiedFunnel = getUnifiedFunnelData(kpiFilters, activeKpiRows);

  // ── Daily aggregation for charts — computed from same filters ─────────────
  const { dailyRows, chartMeta } = useMemo(
    () => getDailyMetrics(activeKpiRows, kpiFilters),
    [activeKpiRows, kpiFilters]
  );

  // ── Age breakdown data ─────────────────────────────────────────────────────
  const ageData = useMemo(
    () => getAgeBreakdown(activeAgeRows, demographicFilters),
    [activeAgeRows, demographicFilters]
  );

  // ── Gender breakdown data ──────────────────────────────────────────────────
  const genderSummary = useMemo(
    () => getGenderSummary(activeAgeRows, demographicFilters),
    [activeAgeRows, demographicFilters]
  );
  const genderDailyData = useMemo(
    () => getGenderDailyData(activeAgeRows, demographicFilters),
    [activeAgeRows, demographicFilters]
  );

  // ── Device breakdown data ──────────────────────────────────────────────────
  const deviceDailyData = useMemo(
    () => getDeviceDailyData(activeDeviceRows, deviceOnlyFilters),
    [activeDeviceRows, deviceOnlyFilters]
  );

  // ── Creative performance analysis data ────────────────────────────────────
  const creativePerformanceData = useMemo(
    () => getCreativePerformanceData(activeKpiRows, kpiFilters, closeCreative.data),
    [activeKpiRows, kpiFilters, closeCreative.data]
  );

  // ── Tab state — driven by route via initialTab prop ────────────────────────
  const activeTab = initialTab;

  // ── Debug output ───────────────────────────────────────────────────────────
  useEffect(() => {
    const filteredRows = applyFilters(activeKpiRows, kpiFilters);
    console.group("[MetaAds] Executive Summary — Debug");
    console.log("Data source:            ", isLive ? "Live (Google Sheets)" : "Mock");
    console.log("Total rows in source:   ", activeKpiRows.length);
    console.log("Rows after filters:     ", filteredRows.length);
    console.log("Date range:             ", filters.dateRange.start, "→", filters.dateRange.end);
    console.log("Total spend:           $" + current.spend.toFixed(2));
    console.log("Total impressions:      " + current.impressions.toLocaleString());
    console.log("Total clicks:           " + current.clicks.toLocaleString());
    console.log("Total leads:            " + current.leads);
    if (liveDebugInfo) {
      console.log("Live — selectedLeadEvent:     ", liveDebugInfo.selectedLeadEvent);
      console.log("Live — selectedPurchaseEvent: ", liveDebugInfo.selectedPurchaseEvent);
      console.log("Live — raw_ads rows:          ", liveDebugInfo.rawAdsCount);
      console.log("Live — raw_actions rows:      ", liveDebugInfo.rawActionsCount);
    }
    if (liveError) {
      console.warn("Live data error (using mock fallback):", liveError);
    }
    console.groupEnd();
  }, [current.spend, current.impressions, current.clicks, current.leads, filters, activeKpiRows, isLive, liveDebugInfo, liveError]);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Inline helpers (used only in Performance tab) ──────────────────────────
  const sd = (n: number, d: number) => d === 0 ? 0 : n / d;
  const cpc          = sd(current.spend, current.clicks);
  const costPerLpv   = sd(current.spend, current.landingPageViews);
  const lpvRate      = sd(current.landingPageViews, current.clicks) * 100;
  const prevCpc        = sd(previous.spend, previous.clicks);
  const prevCostPerLpv = sd(previous.spend, previous.landingPageViews);
  const prevLpvRate    = sd(previous.landingPageViews, previous.clicks) * 100;
  const pctChg = (cur: number, prv: number) => prv === 0 ? 0 : sd(cur - prv, prv) * 100;
  const cpcChange        = pctChg(cpc, prevCpc);
  const costPerLpvChange = pctChg(costPerLpv, prevCostPerLpv);
  const lpvRateChange    = pctChg(lpvRate, prevLpvRate);
  const bounceRateChange = -lpvRateChange;
  // App. Rate and Applications come from CRM (people who submitted applications and entered CRM)
  // NOT from Meta pixel purchases — those are a different event and cause MQLs > Applications paradox

  const GRID = "1fr 72px 1fr 72px 1fr 72px 1fr 72px 1fr 72px 1fr 72px 1fr 72px 1fr 72px 1fr 72px 1fr";

  const HArrow = () => (
    <div className="flex items-center justify-center h-full">
      <svg width="32" height="16" viewBox="0 0 32 16" fill="none" className="opacity-25">
        <line x1="0" y1="8" x2="26" y2="8" stroke="currentColor" strokeWidth="1.5" />
        <path d="M22 4L28 8L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );

  const MetricBridge = ({ label, value, change, invertChange = false }: {
    label: string; value: string; change: number; invertChange?: boolean;
  }) => {
    const good = invertChange ? change < 0 : change > 0;
    const bad  = invertChange ? change > 0 : change < 0;
    return (
      <div className="relative flex items-center justify-center h-full" style={{ zIndex: 20 }}>
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-border/60" />
        <div
          className="relative flex flex-col items-center justify-center rounded-xl border border-border bg-card shadow-md text-center"
          style={{ marginLeft: "-10px", marginRight: "-10px", padding: "6px 8px" }}
        >
          <p className="text-[8px] font-bold text-muted-foreground uppercase tracking-widest leading-none">{label}</p>
          <p className="text-[13px] font-extrabold text-foreground leading-tight mt-1">{value}</p>
          {change !== 0 && (
            <span className={`text-[9px] font-semibold mt-0.5 leading-none ${good ? "text-emerald-400" : bad ? "text-red-400" : "text-muted-foreground"}`}>
              {change > 0 ? "+" : ""}{change.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
    );
  };
  const DArrow = () => (
    <div className="flex justify-center pt-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="text-muted-foreground/30">
        <path d="M5 1V8M5 8L2 5M5 8L8 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
  const UArrow = () => (
    <div className="flex justify-center pb-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="text-muted-foreground/30">
        <path d="M5 9V2M5 2L2 5M5 2L8 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
  const RateCard = ({ label, value, change, idx }: { label: string; value: string; change: number; idx: number }) => {
    const isPos = change > 0;
    const isNeg = change < 0;
    return (
      <div
        className="rounded-xl border border-border/60 bg-muted/30 px-3 py-3 flex flex-col justify-between h-full backdrop-blur-sm"
        style={{ animationDelay: `${0.2 + idx * 0.06}s` }}
      >
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">{label}</p>
        <div className="mt-1.5">
          <span className="text-[17px] font-bold text-foreground leading-none block">{value}</span>
          {change !== 0 && (
            <span className={`text-[11px] font-semibold mt-0.5 block ${
              isPos ? "text-emerald-400" : isNeg ? "text-red-400" : "text-muted-foreground"
            }`}>
              {change > 0 ? "+" : ""}{change.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
    );
  };

  const C = branding.colors;
  const BAR_COLOR  = C.bar ?? "#111827";   // black bars
  const BAR2_COLOR = C.bar ?? "#111827";   // black bars
  const LINE_COLOR = C.chart[0];           // green line (CPL, conv rate, etc.)
  const numFmt  = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(Math.round(v));
  const spndFmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
  const cplFmt  = (v: number) => `$${v.toFixed(0)}`;
  const cpmFmt  = (v: number) => `$${v.toFixed(2)}`;
  const pctFmt  = (v: number) => `${v.toFixed(2)}%`;
  const freqFmt = (v: number) => `${v.toFixed(1)}×`;

  return (
    <div className="space-y-6">
      <PageHeader title="Meta Ads" />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-4"
      >
        {/* ── Data source badge + old filter bar (hidden on overview tab — AdsOverview is self-contained) ── */}
        {activeTab !== "overview" && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {liveLoading && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-[10px] font-semibold text-yellow-400 uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                  Loading live data…
                </span>
              )}
              {!liveLoading && isLive && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live · Google Sheets
                </span>
              )}
              {!liveLoading && !isLive && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--brand-primary)]/10 border border-[var(--brand-primary)]/20 text-[10px] font-semibold text-[var(--brand-primary)] uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)]" />
                  {liveError ? "Mock Data · Live fetch failed" : "Mock Data · Set VITE_META_SHEETS_API_URL in .env"}
                </span>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card/50 px-4 py-3">
              <MetaLeadGenFilterBar
                state={filterState}
                options={mergedOptions}
                adsetOptions={adsetOptions}
                adOptions={adOptions}
                onCampaignChange={setCampaignIds}
                onAdsetChange={setAdsetIds}
                onAdChange={ids => update({ adIds: ids })}
                onAgeChange={v => update({ ages: v })}
                onGenderChange={v => update({ genders: v })}
                onDeviceChange={v => update({ devices: v })}
                onPlatformChange={v => update({ platforms: v })}
                onCreativeTypeChange={v => update({ creativeTypes: v })}
                onCountryChange={v => update({ countries: v })}
                onReset={resetFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            TAB: OVERVIEW
        ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "overview" && <AdsOverview />}

        {/* ═══════════════════════════════════════════════════════════════════════
            TAB: PERFORMANCE
        ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "performance" && (
          <div className="space-y-4">

            {/* ── KPI Flow ── */}
            <div className="space-y-1">
              {/* ROW 1: Volume flow */}
              <div style={{ display: "grid", gridTemplateColumns: GRID, gap: "6px", alignItems: "stretch" }}>
                <KpiCard label="Spend"              value={formatCurrency(current.spend)}         change={changes.spend}            index={0} />
                <MetricBridge label="CPM"           value={formatCpm(current.cpm)}                change={changes.cpm}              invertChange />
                <KpiCard label="Impressions"        value={formatNumber(current.impressions)}      change={changes.impressions}      index={2} />
                <MetricBridge label="CTR"           value={formatPercent(current.ctr)}             change={changes.ctr} />
                <KpiCard label="Clicks"             value={formatNumber(current.clicks)}           change={changes.clicks}           index={4} />
                <MetricBridge label="Bounce Rate"   value={formatPercent(Math.max(0, 100 - lpvRate))} change={bounceRateChange}     invertChange />
                <KpiCard label="Landing Page Views" value={formatNumber(current.landingPageViews)} change={changes.landingPageViews} index={6} />
                <MetricBridge label="Opt-in Rate"   value={formatPercent(current.conversionRate)}  change={changes.conversionRate} />
                <KpiCard label="Opt-ins"            value={formatNumber(current.leads)}            change={changes.leads}            index={8} />
                <MetricBridge label="App. Rate"
                  value={current.leads > 0 && metaAttribution.current > 0 ? formatPercent(metaAttribution.current / current.leads * 100) : "—"}
                  change={pctChg(
                    previous.leads > 0 ? metaAttribution.current / current.leads * 100 : 0,
                    previous.leads > 0 ? metaAttribution.previous / previous.leads * 100 : 0
                  )} />
                <KpiCard label="Applications"
                  value={metaAttribution.loading ? "…" : formatNumber(metaAttribution.current)}
                  change={pctChg(metaAttribution.current, metaAttribution.previous)}
                  index={10} />
                <MetricBridge label="Qual. Rate"
                  value={metaAttribution.current > 0 ? formatPercent(crmFunnel.current.mqls / metaAttribution.current * 100) : "—"}
                  change={pctChg(
                    metaAttribution.current  > 0 ? crmFunnel.current.mqls  / metaAttribution.current  * 100 : 0,
                    metaAttribution.previous > 0 ? crmFunnel.previous.mqls / metaAttribution.previous * 100 : 0
                  )} />
                <KpiCard label="MQLs"
                  value={crmFunnel.loading ? "…" : formatNumber(crmFunnel.current.mqls)}
                  change={pctChg(crmFunnel.current.mqls, crmFunnel.previous.mqls)}
                  index={12} />
                <HArrow />
                <KpiCard label="Closes"
                  value={eocAttribution.loading ? "…" : formatNumber(eocAttribution.totals.closes)}
                  change={0}
                  index={14} />
                <MetricBridge label="Cash/Close"
                  value={eocAttribution.totals.closes > 0 ? formatCurrency(eocAttribution.totals.cashCollected / eocAttribution.totals.closes) : "—"}
                  change={0} />
                <KpiCard label="Cash Collected"
                  value={eocAttribution.loading ? "…" : formatCurrency(eocAttribution.totals.cashCollected)}
                  change={0}
                  index={16} />
                <HArrow />
                <KpiCard label="ROAS"
                  value={current.spend > 0 && eocAttribution.totals.cashCollected > 0 ? `${(eocAttribution.totals.cashCollected / current.spend).toFixed(2)}×` : "—"}
                  change={0}
                  index={18} />
              </div>
            </div>


            {/* ── Daily Performance Insights ── */}
            <div className="flex items-center gap-3 pt-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-2">
                Daily Performance Insights
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Spend · CPL — full width */}
              <div className="col-span-2">
                <ComboChart
                  title="Spend · CPL"
                  data={dailyRows}
                  bar1={{ dataKey: "spend", label: "Spend", color: BAR_COLOR, formatter: formatCurrency, axisFormatter: spndFmt }}
                  line={{ dataKey: "cpl",   label: "CPL",   color: LINE_COLOR, average: chartMeta.averageCpl, formatter: formatCpm, axisFormatter: cplFmt }}
                />
              </div>
              <ComboChart
                title="Impressions · CPM"
                data={dailyRows}
                bar1={{ dataKey: "impressions", label: "Impressions", color: BAR_COLOR,  formatter: formatNumber, axisFormatter: numFmt }}
                line={{ dataKey: "cpm",         label: "CPM",         color: LINE_COLOR, average: chartMeta.averageCpm, formatter: formatCpm, axisFormatter: cpmFmt }}
              />
              <ComboChart
                title="Landing Page Views · Conv. Rate"
                data={dailyRows}
                bar1={{ dataKey: "landingPageViews", label: "LPV",        color: BAR_COLOR,  formatter: formatNumber,   axisFormatter: numFmt }}
                line={{ dataKey: "conversionRate",   label: "Conv. Rate", color: LINE_COLOR, average: chartMeta.averageConversionRate, formatter: formatPercent, axisFormatter: pctFmt }}
              />
              <CampaignFrequencyChart rows={activeKpiRows} filters={kpiFilters} />
              <ComboChart
                title="Spend · Conv. Rate"
                data={dailyRows}
                bar1={{ dataKey: "spend",          label: "Spend",      color: BAR_COLOR,  formatter: formatCurrency, axisFormatter: spndFmt }}
                line={{ dataKey: "conversionRate", label: "Conv. Rate", color: LINE_COLOR, average: chartMeta.averageConversionRate, formatter: formatPercent, axisFormatter: pctFmt }}
              />
              <ComboChart
                title="Clicks · CTR"
                data={dailyRows}
                bar1={{ dataKey: "clicks", label: "Clicks", color: BAR_COLOR, formatter: formatNumber, axisFormatter: numFmt }}
                line={{ dataKey: "ctr",    label: "CTR",    color: LINE_COLOR, average: chartMeta.averageCtr, formatter: formatPercent, axisFormatter: pctFmt }}
              />
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            TAB: BREAKDOWN
        ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "breakdown" && (
          <div className="space-y-8">

            <GenderBreakdownSection summary={genderSummary} dailyData={genderDailyData} />
            <AgeBreakdownSection data={ageData} />
            <DeviceBreakdownSection dailyData={deviceDailyData} />

          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            TAB: CREATIVE
        ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "creative" && (
          <div className="space-y-10">

            {/* ── Creative Performance Heatmap ── */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-2 px-3">
                <div className="w-5 h-5 rounded-md bg-[var(--brand-primary)]/15 flex items-center justify-center">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <rect x="1" y="1" width="4" height="4" rx="0.5" stroke="var(--brand-primary)" strokeWidth="1.2" />
                    <rect x="6" y="1" width="4" height="4" rx="0.5" stroke="var(--brand-primary)" strokeWidth="1.2" />
                    <rect x="1" y="6" width="4" height="4" rx="0.5" stroke="var(--brand-primary)" strokeWidth="1.2" />
                    <rect x="6" y="6" width="4" height="4" rx="0.5" stroke="var(--brand-primary)" strokeWidth="1.2" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
                  Creative Performance Heatmap
                </span>
              </div>
              <div className="h-px flex-1 bg-border" />
            </div>

            <CreativeHeatmapSection
              rows={activeKpiRows}
              filters={filters}
              closeAttribution={closeCreative.data}
              eocAttribution={eocAttribution.data}
              optinAdMap={optinAdAttribution.rawMap}
            />

            {/* ── Creative Performance Analysis ── */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-2 px-3">
                <div className="w-5 h-5 rounded-md bg-[var(--brand-primary)]/15 flex items-center justify-center">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path d="M1 9L3.5 5.5L6 7.5L9 2" stroke="var(--brand-primary)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
                  Creative Performance Analysis
                </span>
              </div>
              <div className="h-px flex-1 bg-border" />
            </div>

            <CreativePerformanceSection
              data={creativePerformanceData}
              rows={activeKpiRows}
              filters={filters}
              optinAdMap={optinAdAttribution.rawMap}
            />

            {/* ── Individual Creative Drill-Down ── */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-2 px-3">
                <div className="w-5 h-5 rounded-md bg-[var(--brand-primary)]/15 flex items-center justify-center">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path d="M5.5 1L5.5 10M5.5 1C5.5 1 3 3.5 1 5.5M5.5 1C5.5 1 8 3.5 10 5.5" stroke="var(--brand-primary)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
                  Individual Creative Drill-Down
                </span>
              </div>
              <div className="h-px flex-1 bg-border" />
            </div>

            <IndividualCreativeFunnelSection
              rows={activeKpiRows}
              filters={filters}
              closeAttribution={closeCreative.data}
              optinAdMap={optinAdAttribution.rawMap}
            />
          </div>
        )}

      </motion.div>
    </div>
  );
}
